'use client';

import { cn } from '@shared/components/shadcn';
import ChevronRight from '@shared/icons/ChevronRight';
import type { IconProps } from '@shared/icons/types';

type SelectionCardProps = {
  icon: React.ComponentType<IconProps>;
  label: string;
  description?: string;
  onClick: () => void;
  className?: string;
  hierarchy?: 'default';
  'data-testid'?: string;
  'data-track-name'?: string;
};

/**
 * SelectionCard component based on NavigationPod's action variant.
 * Displays an icon, label, description, and chevron in a clickable card format.
 * Unlike NavigationPod, this component includes description text and uses a button instead of a Link.
 */
function SelectionCard({
  icon: Icon,
  label,
  description,
  onClick,
  className,
  hierarchy = 'default',
  'data-testid': dataTestId,
  'data-track-name': dataTrackName,
}: SelectionCardProps) {
  const variantStyles = { wrapper: '', iconWrapper: '', icon: '', chevron: '' };
  const styles = {
    variants: {
      default: {
        ...variantStyles,
        wrapper: cn(
          'flex items-center text-left justify-between gap-4 w-full p-4 paragraph-strong text-foreground-inverse bg-container-base rounded-lg border-1 border-border hover:border-border-active',
          className
        ),
        iconWrapper: 'flex flex-col items-center justify-center shrink-0 w-10 h-10 bg-accent-green rounded-full',
        icon: 'w-6 h-6 text-secondary',
        chevron: 'shrink-0 w-5 h-5 text-foreground-subtle',
      },
    },
  };

  const classes = styles.variants[hierarchy];
  const Chevron = ChevronRight as React.ComponentType<IconProps>;
  const IconComponent = Icon as React.ComponentType<IconProps>;

  return (
    <button onClick={onClick} className={classes.wrapper} data-testid={dataTestId} data-track-name={dataTrackName} type="button">
      <div className="flex gap-5 flex-1">
        <div className={classes.iconWrapper}>
          <IconComponent className={classes.icon} />
        </div>
        <div className="flex-1 text-foreground">
          <div className={description ? 'paragraph-strong mb-1' : 'paragraph-strong'}>{label}</div>
          {description ? <div className="paragraph-s">{description}</div> : null}
        </div>
      </div>
      <Chevron size="regular" className={classes.chevron} />
    </button>
  );
}

export { SelectionCard };
export type { SelectionCardProps };
